import React, { useEffect } from 'react';
import { Text, Button, type InputTextProps } from '@companycam/slab-web';

import {
  type PublicProfileObject,
  type GooglePlaceDetails,
} from 'data/v3/data-contracts';
import styled from 'styled-components';
import { translate } from 'javascripts/translations/i18n';
import { getGoogleReviewUrl } from 'components/reviews/shared/utils/getGoogleReviewUrl';
import { Row } from '../CommonComponents';
import {
  GooglePlaceAutocomplete,
  getAddressComponent,
} from 'components/shared/GooglePlaceAutocomplete';
import { type UseProfileFormReturn } from '../useProfileForm';
import { useGetPlaceDetails } from 'data/v3/googlePlaces';

const LocationInfoContainer = styled.div`
  display: flex;
  flex: 1;
`;

const PublicProfileData = styled.div`
  flex: 1;
`;

const PublicProfileDataContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: var(--cc_color_background_2);
  padding: var(--cc_size_spacing_l);
  border-radius: var(--cc_size_border_radius_l);
  flex-wrap: wrap;
`;

const GoogleSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: var(--cc_size_spacing_l);
  border-top: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
`;

export const GooglePlaceSection = ({
  isLoading,
  handlePlaceChanged,
  inputMessage,
  formMethods,
  incompletePublicProfile,
}: {
  isLoading: boolean;
  handlePlaceChanged: (place: GooglePlaceDetails) => void;
  inputMessage: {
    message: string;
    status: InputTextProps['status'];
  };
  formMethods: UseProfileFormReturn;
  incompletePublicProfile?: PublicProfileObject;
}) => {
  const {
    data: incompleteProfilePlaceDetails,
    isLoading: isIncompleteProfilePlaceDetailsLoading,
  } = useGetPlaceDetails(incompletePublicProfile?.googlePlaceId);

  useEffect(() => {
    if (incompleteProfilePlaceDetails) {
      const details = incompleteProfilePlaceDetails.data.placeDetails;

      formMethods.setValue('name', details.displayName.text);
      formMethods.setValue('googlePlaceId', details.id);
      if (details.websiteUri) {
        formMethods.setValue('website', new URL(details.websiteUri).origin);
      }
      formMethods.setValue('phoneNumber', details.nationalPhoneNumber);

      // address
      formMethods.setValue(
        'addressLine1',
        getAddressComponent(details.addressComponents, [
          'street_number',
          'route',
        ]),
      );
      formMethods.setValue(
        'addressLine2',
        getAddressComponent(details.addressComponents, ['subpremise']),
      );
      formMethods.setValue(
        'city',
        getAddressComponent(details.addressComponents, ['locality']),
      );
      formMethods.setValue(
        'state',
        getAddressComponent(details.addressComponents, [
          'administrative_area_level_1',
        ]),
      );
      formMethods.setValue(
        'postalCode',
        getAddressComponent(details.addressComponents, ['postal_code']),
      );
    }
  }, [formMethods, incompleteProfilePlaceDetails]);

  if (isLoading || isIncompleteProfilePlaceDetailsLoading) {
    return null;
  }

  if (incompleteProfilePlaceDetails) {
    const name =
      incompleteProfilePlaceDetails.data.placeDetails.displayName.text;
    const address =
      incompleteProfilePlaceDetails.data.placeDetails.formattedAddress;
    const phone =
      incompleteProfilePlaceDetails.data.placeDetails.nationalPhoneNumber;
    const website = incompleteProfilePlaceDetails.data.placeDetails.websiteUri
      ? new URL(incompleteProfilePlaceDetails.data.placeDetails.websiteUri)
          .origin
      : null;

    return (
      <PublicProfileDataContainer>
        <LocationInfoContainer>
          <PublicProfileData>
            <Text as="p" weight={700} family="heading">
              {name}
            </Text>
            {address ? (
              <Text as="p" family="heading">
                {address}
              </Text>
            ) : null}
            {phone ? (
              <Text as="p" family="heading">
                {phone}
              </Text>
            ) : null}
            {website ? (
              <Text as="p" family="heading">
                {website}
              </Text>
            ) : null}
          </PublicProfileData>
          <Button
            data-testid="open-google-maps-button"
            href={getGoogleReviewUrl(
              incompleteProfilePlaceDetails.data.placeDetails.id,
            )}
            target="_blank"
            rel="noreferrer"
            color="subtle"
            icon={{
              name: 'open-in-new',
              position: 'after',
            }}
          >
            {translate('project.reviews.reviewProfile.openGoogleMaps')}
          </Button>
        </LocationInfoContainer>
      </PublicProfileDataContainer>
    );
  }

  return (
    <Row>
      <GoogleSearchContainer>
        <Text as="h2" size="m" family="heading" weight="700">
          {translate('portfolio.profile.googleBusinessSearch')}
        </Text>
        <Text as="p" size="s" family="heading" ccMargin="none none xs">
          {translate('portfolio.profile.googleBusinessSearchMessage')}
        </Text>
      </GoogleSearchContainer>
      <GooglePlaceAutocomplete
        onPlaceSelect={handlePlaceChanged}
        placeholder={translate('portfolio.profile.searchGoogle')}
        message={inputMessage.message}
        status={inputMessage.status}
        defaultValue={formMethods.watch('name')}
      />
    </Row>
  );
};
